import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  getGetApiV1MissionsBoardQueryKey,
  usePostApiV1MissionsMissionIdClose,
  usePostApiV1MissionsMissionIdPublish,
  usePostApiV1MissionsMissionIdRounds,
  usePostApiV1MissionsMissionIdRoundsCurrentEnd,
  usePostApiV1MissionsMissionIdSubmitToCommittee,
} from '@/shared/api/generated/missions/missions';
import type {
  GetApiV1MissionsBoardParams,
  MissionListItemDto,
  MissionOutcome,
} from '@/shared/api/generated/model';
import { usePostApiV1MissionsMissionIdCertificates } from '@/shared/api/generated/certificates/certificates';
import { problemDetail } from '@/shared/api/problem';
import { SystemRoles, useRoles } from '@/features/auth/model/use-roles';
import { useLocale } from '@/shared/lib/i18n/locale-context';
import { Button } from '@/shared/ui/Button';
import { CloseMissionDialog } from '@/features/missions/ui/CloseMissionDialog';
import { StartRoundDialog } from '@/features/missions/ui/StartRoundDialog';

/**
 * The actions a control room can take on one mission (§23.1).
 *
 * ⚠️ Every action is gated twice: by the mission's STATUS and by the operator's ROLE.
 *
 * The status gate mirrors the domain exactly — `Mission.Close` accepts only `Approved` or `Active`,
 * `Mission.StartRound` only `Active` — so the panel never offers a click the server will answer 409.
 * The role gate is cosmetic (see `useRoles`): the API is the authority, and this only avoids drawing a
 * button whose request is certain to come back 403.
 *
 * The two gates are kept separate on purpose. A status that changes is data; a role that changes is
 * authorisation, and collapsing them into one condition is how "may this operator act" quietly becomes
 * "is this mission in the right state".
 */
export function MissionActions({
  mission,
  params,
}: {
  mission: MissionListItemDto;
  params: GetApiV1MissionsBoardParams;
}) {
  const { t } = useLocale();
  const { hasAny } = useRoles();
  const queryClient = useQueryClient();

  const [closing, setClosing] = useState(false);
  const [startingRound, setStartingRound] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [thanked, setThanked] = useState<string | null>(null);

  const refresh = async () => {
    setError(null);
    await queryClient.invalidateQueries({ queryKey: getGetApiV1MissionsBoardQueryKey(params) });
  };

  const onError = (cause: unknown) => setError(problemDetail(cause) ?? t.missions.actionFailed);

  const publish = usePostApiV1MissionsMissionIdPublish({
    mutation: { onSuccess: refresh, onError },
  });
  const close = usePostApiV1MissionsMissionIdClose({
    mutation: {
      onSuccess: async () => {
        setClosing(false);
        await refresh();
      },
      onError,
    },
  });
  const startRound = usePostApiV1MissionsMissionIdRounds({
    mutation: {
      onSuccess: async () => {
        setStartingRound(false);
        await refresh();
      },
      onError,
    },
  });
  const endRound = usePostApiV1MissionsMissionIdRoundsCurrentEnd({
    mutation: { onSuccess: refresh, onError },
  });

  // ⚠️ **§23.2's own path, and nothing called it until now.**
  //
  // `POST /missions/{id}/submit-to-committee` has existed on the server and in the generated client
  // all along, and no screen in either the panel or the app invoked it — so the only action a draft
  // offered was «تصريح وبدء», the bypass. The committee box on the mission page counted votes on a
  // report the committee could never be shown. Measured on 27 August 2026, driving the whole
  // scenario end to end.
  //
  // The window is left null: §23.2 makes it configurable from the dashboard and the server falls
  // back to the committee settings, which is where the association changes it.
  const submitToCommittee = usePostApiV1MissionsMissionIdSubmitToCommittee({
    mutation: { onSuccess: refresh, onError },
  });

  const thank = usePostApiV1MissionsMissionIdCertificates({
    mutation: {
      // ⚠️ Says the COUNT, and says it even when the count is zero. Running this twice is the normal
      // way it gets used — somebody credited after the first run — and a button that reports nothing
      // leaves an operator pressing it again to find out whether it worked.
      onSuccess: (result) =>
        setThanked(
          result.issued
            ? t.missions.certificatesIssued.replace('{n}', String(result.issued))
            : t.missions.certificatesAlreadyIssued,
        ),
      onError,
    },
  });

  const busy =
    publish.isPending ||
    submitToCommittee.isPending ||
    close.isPending ||
    startRound.isPending ||
    endRound.isPending ||
    thank.isPending;

  // §23.2: publishing without a vote belongs to the Head of Control. Desk Control follows, ends and
  // adds members, but does not decide that a search happens.
  const isHeadOfControl = hasAny(SystemRoles.HeadOfControl, SystemRoles.SystemAdministrator);

  const mayPublish =
    isHeadOfControl &&
    (mission.status === 'Draft' || mission.status === 'PendingCommittee');

  // ⚠️ **Approved is a separate button, because a mission the COMMITTEE approved lands there.**
  //
  // The publish gate above deliberately excludes `Approved` — publishing is the decision, and it is
  // already made. But the panel then offered a committee-approved mission nothing except «إقفال»:
  // the search could not be started by the person responsible for starting it. What covered the gap
  // was an accident — the first volunteer to join starts the mission — so the start time recorded on
  // the report was whenever somebody happened to tap join. Measured.
  const mayStart = isHeadOfControl && mission.status === 'Approved';

  // §23.2 again: only a missing-person DRAFT goes to the committee. A stranded rescue is born
  // approved and a sub-mission has nothing to deliberate — the server says the same and answers 409.
  const maySubmitToCommittee =
    isHeadOfControl && mission.status === 'Draft' && mission.type === 'Missing';

  // §23.1: ending a mission is Desk Control's too — the client answered "الكل".
  const mayAct = hasAny(
    SystemRoles.DeskControl,
    SystemRoles.HeadOfControl,
    SystemRoles.SystemAdministrator,
  );

  const mayClose = mayAct && (mission.status === 'Approved' || mission.status === 'Active');

  // ⚠️ Only once the search is CLOSED, which mirrors the server exactly (§23.6). A certificate
  // thanks somebody for a finished piece of work; offered mid-search the button would hand out the
  // association's seal on an outcome nobody knows yet, and the API would refuse it anyway.
  const mayThank = mayAct && mission.status === 'Closed';
  const mayStartRound = mayAct && mission.status === 'Active' && mission.type !== 'SubMission';
  const mayEndRound = mayStartRound;

  const anyAction =
    mayPublish || maySubmitToCommittee || mayStart || mayClose || mayStartRound || mayThank;

  return (
    <div className="flex flex-col gap-1.5">
      {anyAction ? (
        <div className="flex flex-wrap gap-2">
          {maySubmitToCommittee ? (
            <Button
              variant="ghost"
              disabled={busy}
              onClick={() =>
                submitToCommittee.mutate({
                  missionId: mission.missionId,
                  data: { votingWindowMinutes: null },
                })
              }
            >
              {t.missions.submitToCommittee}
            </Button>
          ) : null}

          {mayPublish ? (
            <Button
              disabled={busy}
              onClick={() =>
                // `start: true` — a mission the control room has just decided to run should be running,
                // not sitting approved and waiting for a second click nobody knows to make.
                publish.mutate({ missionId: mission.missionId, data: { start: true } })
              }
            >
              {t.missions.publish}
            </Button>
          ) : null}

          {mayStart ? (
            <Button
              disabled={busy}
              onClick={() =>
                // The same endpoint: its handler treats an already-approved mission as approved and
                // only starts it, which is exactly what this button means.
                publish.mutate({ missionId: mission.missionId, data: { start: true } })
              }
            >
              {t.missions.startMission}
            </Button>
          ) : null}

          {mayStartRound ? (
            <Button variant="ghost" disabled={busy} onClick={() => setStartingRound(true)}>
              {t.missions.startRound}
            </Button>
          ) : null}

          {mayEndRound ? (
            <Button
              variant="ghost"
              disabled={busy}
              onClick={() => endRound.mutate({ missionId: mission.missionId })}
            >
              {t.missions.endRound}
            </Button>
          ) : null}

          {mayThank ? (
            <Button
              variant="ghost"
              disabled={busy}
              onClick={() => thank.mutate({ missionId: mission.missionId })}
            >
              {t.missions.issueCertificates}
            </Button>
          ) : null}

          {mayClose ? (
            <Button variant="danger" disabled={busy} onClick={() => setClosing(true)}>
              {t.missions.close}
            </Button>
          ) : null}
        </div>
      ) : (
        <span className="text-muted-foreground">{t.missions.noActions}</span>
      )}

      {/* Only when no dialog is open. While one is, the message goes inside it — the overlay covers
          the row and Radix marks it aria-hidden. */}
      {error && !closing && !startingRound ? (
        <span role="alert" className="text-xs text-danger">
          {error}
        </span>
      ) : null}

      {thanked ? (
        <span role="status" className="text-xs text-success-600">
          {thanked}
        </span>
      ) : null}

      {closing ? (
        <CloseMissionDialog
          missionTitle={mission.title}
          missionType={mission.type}
          open
          busy={close.isPending}
          serverError={error}
          onOpenChange={(next) => {
            if (!next) {
              setClosing(false);
            }
          }}
          onConfirm={(outcome: MissionOutcome, summary) =>
            close.mutate({ missionId: mission.missionId, data: { outcome, summary } })
          }
        />
      ) : null}

      {startingRound ? (
        <StartRoundDialog
          missionTitle={mission.title}
          open
          busy={startRound.isPending}
          serverError={error}
          onOpenChange={(next) => {
            if (!next) {
              setStartingRound(false);
            }
          }}
          onConfirm={(title) =>
            startRound.mutate({ missionId: mission.missionId, data: { title, areaRing: null } })
          }
        />
      ) : null}
    </div>
  );
}
